/**
 * Builds URLs for files served out of /public.
 *
 * Two things this handles that a hand-written string doesn't:
 *
 * 1. `PUBLIC_URL`. If the site is ever deployed under a sub-path (a project
 *    GitHub Pages URL, say) a hard-coded "/img/..." breaks, because it resolves
 *    against the domain root rather than the app's base. CRA sets
 *    process.env.PUBLIC_URL at build time for exactly this.
 *
 * 2. Relative paths under HashRouter. The app lives at "/#/", so a bare
 *    "01.png" resolves to "/01.png" — the hash contributes no directory. Every
 *    asset path therefore has to be rooted, and forgetting that produces a
 *    silent 404 with a broken-image icon and no error anywhere.
 */

const BASE = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");

const isAbsolute = (path: string) =>
  /^(https?:)?\/\//i.test(path) || path.startsWith("data:") || path.startsWith("blob:");

/** "/img/svg/logo.svg" or "img/svg/logo.svg" -> a URL that works anywhere. */
export const assetUrl = (path: string): string => {
  if (!path) return "";
  if (isAbsolute(path)) return path;
  return `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
};

/** Where gallery screenshots live, matching public/img/game/README.txt. */
export const GAME_IMAGE_DIR = "/img/game";

/**
 * Resolves a gallery entry's `src`.
 *
 * A bare filename ("01.png") is taken to mean a file in public/img/game, which
 * is what the README tells you to do and the mistake that's easiest to make.
 * Anything containing a slash is treated as a full public path and left alone,
 * so you can still point at an image elsewhere.
 */
export const gameImageUrl = (src: string): string => {
  if (!src) return "";
  if (isAbsolute(src)) return src;
  return src.includes("/") ? assetUrl(src) : assetUrl(`${GAME_IMAGE_DIR}/${src}`);
};
